// project imports
import { Theme } from '@mui/material/styles';

export default function componentStyleOverrides(theme: Theme, borderRadius: number, outlinedFilled: boolean) {
    const mode = theme.palette.mode;
    const bgColor = mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[50];
    const menuSelectedBack = mode === 'dark' ? theme.palette.primary.main + 15 : theme.palette.primary.light;
    const menuSelected = mode === 'dark' ? theme.palette.primary.main : theme.palette.primary[800];

    return {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                    h4: 'h2',
                    h5: 'p',
                    h6: 'p',
                    subtitle1: 'p',
                    subtitle2: 'p'
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    backgroundColor: theme.palette.primary.dark,
                    borderRadius: '8px',
                    boxShadow: 'none'
                },
                sizeSmall: {
                    padding: '8px 16px'
                },
                sizeMedium: {
                    padding: '10px 16px'
                },
                sizeLarge: {
                    padding: '16px'
                },
                contained: {
                    backgroundColor: theme.palette.primary.dark
                },
                outlined: {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    backgroundColor: 'transparent',
                    fontWeight: 400,
                    color: theme.palette.grey[600]
                }
            }
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    borderColor: theme.palette.primary.light
                },
                rounded: {
                    borderRadius: `${borderRadius}px`
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    color: theme.palette.text.dark,
                    padding: '24px'
                    //backgroundColor: 'red'
                },
                title: {
                    fontSize: '1.125rem'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '24px',
                    borderColor: theme.palette.primary.light
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    alignItems: 'center'
                },
                outlined: {
                    border: '1px dashed'
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    boxShadow: '0px 24px 38px 0px rgba(9, 15, 37, 0.07)'
                }
            }
        },
        MuiList: {
            styleOverrides: {
                root: {
                    '> li': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                    },
                    '> li > span': {
                        color: theme.palette.grey[600],
                        fontSize: '0.75rem'
                    }
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem'
                },
                '& .Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary[800]
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    padding: '14px 16px',
                    '&.Mui-selected': {
                        color: menuSelected,
                        backgroundColor: menuSelectedBack,
                        '&:hover': {
                            backgroundColor: menuSelectedBack
                        },
                        '& .MuiListItemIcon-root': {
                            color: menuSelected
                        }
                    },
                    '&:hover': {
                        backgroundColor: menuSelectedBack,
                        color: menuSelected,
                        '& .MuiListItemIcon-root': {
                            color: menuSelected
                        }
                    }
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    '> svg': {
                        strokeWidth: 1.5
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: theme.palette.text.dark,
                    '&::placeholder': {
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem'
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    background: outlinedFilled ? bgColor : 'transparent',
                    borderRadius: `${borderRadius}px`,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: mode === 'dark' ? theme.palette.text.primary + 28 : theme.palette.grey[400]
                    },
                    '&:hover $notchedOutline': {
                        borderColor: theme.palette.primary.light
                    },
                    '&.MuiInputBase-multiline': {
                        padding: 1
                    }
                },
                input: {
                    fontWeight: 500,
                    fontSize: '14px',
                    height: 'auto',
                    maxHeight: '48px',
                    background: outlinedFilled ? bgColor : 'transparent',
                    padding: '15.5px 14px',
                    borderRadius: `${borderRadius}px`,
                    '&.MuiInputBase-inputSizeSmall': {
                        padding: '12px 16px',
                        '&.MuiInputBase-inputAdornedStart': {
                            paddingLeft: 0
                        }
                    }
                },
                inputAdornedStart: {
                    paddingLeft: 4
                },
                notchedOutline: {
                    borderRadius: `${borderRadius}px`,
                    borderColor: theme.palette.primary.light
                }
            }
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: mode === 'dark' ? theme.palette.text.primary + 50 : theme.palette.grey[300]
                    }
                },
                mark: {
                    backgroundColor: theme.palette.background.paper,
                    width: '4px'
                },
                valueLabel: {
                    color: mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiAutocomplete-tag': {
                        background: mode === 'dark' ? theme.palette.text.primary + 20 : theme.palette.primary.light,
                        borderRadius: 4,
                        color: theme.palette.text.dark,
                        '.MuiChip-deleteIcon': {
                            color: theme.palette.primary.dark
                        }
                    },
                    '& .MuiOutlinedInput-root': {
                        padding: '8px !important',
                        paddingRight: '30px !important'
                    }
                },
                popper: {
                    borderRadius: `${borderRadius}px`,
                    boxShadow: '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)'
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: theme.palette.divider,
                    opacity: mode === 'dark' ? 0.2 : 1
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    '&:hover > div::before': {
                        borderBottom: 'none'
                    },
                    '&::before': {
                        borderBottom: 'none !important'
                    },
                    '&::after': {
                        borderBottom: 'none'
                    }
                },
                select: {
                    '&.MuiSelect-standard:focus': {
                        backgroundColor: 'transparent',
                        outline: '2px solid',
                        outlineColor: theme.palette.primary.main,
                        outlineOffset: '2px',
                        borderRadius: '4px'
                    }
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    color: mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.dark,
                    background: mode === 'dark' ? theme.palette.text.primary : theme.palette.primary[200]
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    '&.MuiChip-deletable .MuiChip-deleteIcon': {
                        color: theme.palette.primary.main
                    }
                }
            }
        },
        MuiTimelineContent: {
            styleOverrides: {
                root: {
                    color: theme.palette.text.dark,
                    fontSize: '16px'
                }
            }
        },
        MuiTreeItem: {
            styleOverrides: {
                label: {
                    marginTop: 14,
                    marginBottom: 14
                }
            }
        },
        MuiTimelineDot: {
            styleOverrides: {
                root: {
                    boxShadow: 'none'
                }
            }
        },
        MuiInternalDateTimePickerTabs: {
            styleOverrides: {
                tabs: {
                    backgroundColor: mode === 'dark' ? theme.palette.dark[900] : theme.palette.primary.light,
                    '& .MuiTabs-flexContainer': {
                        borderColor: mode === 'dark' ? theme.palette.text.primary + 20 : theme.palette.primary[200]
                    },
                    '& .MuiTab-root': {
                        color: mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[900]
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.dark
                    },
                    '& .Mui-selected': {
                        color: theme.palette.primary.dark
                    }
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    borderBottom: '1px solid',
                    borderColor: mode === 'dark' ? theme.palette.text.primary + 20 : theme.palette.primary.light
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    color: theme.palette.grey[700],
                    '&.Mui-selected': {
                        color: theme.palette.primary.dark
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    padding: '12px 0 12px 0'
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: mode === 'dark' ? theme.palette.text.primary + 15 : theme.palette.primary.light,
                    '&.MuiTableCell-head': {
                        fontSize: '0.8rem',
                        color: theme.palette.grey[900],
                        fontWeight: 500,

                        '&:focus-visible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: '3px',
                            borderRadius: '4px',
                            backgroundColor: theme.palette.action.hover
                        }
                    },
                    border: 'none',
                    color: 'inherit'
                }
            }
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    padding: '0 8px',
                    '&:hover, &:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '3px',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        '& .MuiTableSortLabel-icon': {
                            opacity: 0.8
                        }
                    }
                }
            }
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    width: 'auto'
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    borderColor: mode === 'dark' ? theme.palette.text.primary + 15 : theme.palette.grey[100],
                    borderBottom: `1px solid ${mode === 'dark' ? theme.palette.text.primary + 15 : theme.palette.grey[100]}`,
                    '&.Mui-selected': {
                        backgroundColor: '#f09628'
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: theme.palette.background.paper,
                    background: theme.palette.text.primary,
                    fontSize: '0.875rem'
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.25rem'
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginTop: '8px'
                }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                content: {
                    fontWeight: 700,
                    fontSize: '14px'
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    fontSize: '14px'
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontWeight: 400,
                    fontSize: '14px',
                    color: theme.palette.grey[900],
                    '&.Mui-disabled': {
                        color: theme.palette.grey[600]
                    }
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: theme.palette.grey[800],
                    '&.Mui-disabled': {
                        color: theme.palette.grey[600]
                    }
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: `${theme.palette.primary.dark} !important`
                    }
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                thumb: {
                    boxShadow: 'none'
                },
                track: {
                    backgroundColor: theme.palette.grey[500],
                    opacity: 1
                },
                switchBase: {
                    color: `${theme.palette.grey[200]} !important`
                },
                root: {
                    '.MuiSwitch-track': {
                        backgroundColor: `${theme.palette.grey[500]} !important`
                    },
                    '& .Mui-checked': {
                        color: `${theme.palette.primary.dark} !important`
                    },
                    '& .Mui-checked+.MuiSwitch-track': {
                        backgroundColor: `${theme.palette.primary.main} !important`,
                        border: `1px solid ${theme.palette.primary.dark} !important`
                    }
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: theme.palette.primary.main
                }
            }
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    fontWeight: 700
                }
            }
        }
    };
}
